import React, { FunctionComponent, useState, useRef, useEffect } from 'react';
import {
  Button,
  Col,
  FormCheck,
  FormControl,
  Modal,
  Row,
  Table,
} from 'react-bootstrap';
import { Form } from 'react-final-form';
import { connect, ConnectedProps } from 'react-redux';
import apis from '../../apis';
import {
  AuthedLayout,
  NumberField,
  TextField,
  InputTransformField,
  DateTimePicker,
} from '../../components';
import { Student, TransferInfo } from '../../model';
import { ErrorDispatches } from '../../redux/Dispatches';
import { ApplicationState } from '../../redux/States';
import { getGradeMinMax } from '../../utils';
import { I18N } from '../../i18n-raw';
import { DateTime } from 'luxon';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import { toast } from 'react-toastify';

const mapState = (state: ApplicationState) => ({
  ...state.auth,
});
const mapDispatch = { ...ErrorDispatches };
const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector>;

const transferInPage: FunctionComponent<Props> = ({
  user: { currentRank, currentSemester, semesters, currentSchool },
  catchErrorForModal,
}) => {
  const [transferInfo, setTransfrInfo] = useState<TransferInfo>();
  const [transferInfoSHIS, setTransferInfoSHIS] = useState<TransferInfo>();
  const [transferRequestList, setTransferRequestList] = useState<DateTime[]>(
    []
  );
  const [student, setStudent] = useState<Student>();
  const [studentSHIS, setStudentSHIS] = useState<Student>();
  const [modalContent, setModalContent] = useState<string>();
  const [selectedSem, setSelectedSem] = useState<string>(currentSemester);
  const [birth, setBirth] = useState<DateTime>(); // 學生生日
  const [transferDisabled, setTransferDisabled] = useState<boolean>(true);
  const [transferInBtnDisabled, setTransferInBtnDisabled] =
    useState<boolean>(false);
  const pidRef = useRef<HTMLInputElement>(null);

  const studentContent = student ? (
    <>
      <Table bordered className="student-phi">
        <tbody>
          <tr>
            <th>身份證</th>
            <td>{student.pid}</td>
          </tr>
          <tr>
            <th>學生</th>
            <td>{student.name}</td>
          </tr>
          <tr>
            <th>性別</th>
            <td>{student.sex}</td>
          </tr>
          <tr>
            <th>生日</th>
            <td>{student.birth?.toFormat('yyyy/MM/dd')}</td>
          </tr>
          <tr>
            <th>父親</th>
            <td>{student.dad}</td>
          </tr>
          <tr>
            <th>母親</th>
            <td>{student.mom}</td>
          </tr>
          <tr>
            <th>連絡電話</th>
            <td>{student.tel}</td>
          </tr>
          <tr>
            <th>緊急連絡</th>
            <td>{student.erTel}</td>
          </tr>
          <tr>
            <th>身份</th>
            <td>{student.aborigine}</td>
          </tr>
        </tbody>
      </Table>
    </>
  ) : (
    <></>
  );
  const studentContentSHIS = studentSHIS ? (
    <>
      <Table bordered className="student-phi">
        <tbody>
          <tr>
            <th>身份證</th>
            <td>{studentSHIS.pid}</td>
          </tr>
          <tr>
            <th>學生</th>
            <td>{studentSHIS.name}</td>
          </tr>
          <tr>
            <th>性別</th>
            <td>{studentSHIS.sex}</td>
          </tr>
          <tr>
            <th>生日</th>
            <td>{studentSHIS.birth?.toFormat('yyyy/MM/dd')}</td>
          </tr>
          <tr>
            <th>父親</th>
            <td>{studentSHIS.dad}</td>
          </tr>
          <tr>
            <th>母親</th>
            <td>{studentSHIS.mom}</td>
          </tr>
          <tr>
            <th>連絡電話</th>
            <td>{studentSHIS.tel}</td>
          </tr>
          <tr>
            <th>緊急連絡</th>
            <td>{studentSHIS.erTel}</td>
          </tr>
          <tr>
            <th>身份</th>
            <td>{studentSHIS.aborigine}</td>
          </tr>
        </tbody>
      </Table>
    </>
  ) : (
    <></>
  );
  const { min, max } = getGradeMinMax(currentRank);
  const classGradeContent = transferInfo ? (
    <>
      <hr />
      <Form
        onSubmit={({ sid, seat, classGrade, classNo }) => {
          if (!seat || !classGrade || !classNo) {
            setModalContent('年級、班級、座號為必填欄位，請確認是否已輸入');
            return;
          }
          const { year, sem } = semesters[selectedSem];
          setTransferInBtnDisabled(true);
          apis
            .transferIn(transferInfo.transferId, {
              sid: sid ?? ' ',
              seat,
              classGrade: +classGrade,
              classNo,
              year,
              sem,
            })
            .then(() => {
              setStudent(undefined);
              setTransfrInfo(undefined);
              setModalContent('轉入成功');
              setTransferRequestList([]);
              setTransferInBtnDisabled(false);
            })
            .catch((e) => {
              catchErrorForModal(e);
            });
        }}
        render={(props) => {
          const { handleSubmit } = props;
          return (
            <>
              <Row className="mb-2">
                <Col xs={1}>轉入學年：</Col>
                {Object.entries(semesters)
                  .filter(([k]) => k >= currentSemester)
                  .map(([n]) => {
                    return (
                      <Col key={`sem-${n}`} xs={1}>
                        <FormCheck
                          type="radio"
                          label={n}
                          name="setSemester"
                          checked={n === selectedSem}
                          onChange={(e) => {
                            setSelectedSem(n);
                          }}
                        />
                      </Col>
                    );
                  })}
              </Row>
              <Row className="mb-2">
                <Col xs={1}>轉入學號:</Col>
                <Col xs={5} className="mr-2">
                  <TextField property="sid" />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={1}>轉入年級:</Col>
                <Col xs={4} className="mr-2">
                  <InputTransformField
                    property="classGrade"
                    type="number"
                    min={min}
                    max={max}
                    transform={(v) =>
                      (+v >= min && +v <= max && I18N.Grades[+v]) || '未知年級'
                    }
                    validate={(v) => {
                      return +v >= min && +v <= max
                        ? undefined
                        : `範圍應在${min}~${max}之間`;
                    }}
                  />
                </Col>
                <Col xs={1}>轉入班級代號:</Col>
                <Col xs={2} className="mr-2">
                  <NumberField property="classNo" min={1} max={50} />
                </Col>
                <Col xs={1}>座號:</Col>
                <Col xs={2} className="mr-2">
                  <NumberField property="seat" min={1} max={99} />
                </Col>
              </Row>
              <Row className="mb-2">
                <Button onClick={handleSubmit} disabled={transferInBtnDisabled}>
                  轉入
                </Button>
              </Row>
            </>
          );
        }}
      />
    </>
  ) : (
    <></>
  );
  const classGradeContentSHIS = transferInfoSHIS ? (
    <>
      <hr />
      <Form
        onSubmit={({ sid, seat, classGrade, classNo }) => {
          const { year, sem } = semesters[selectedSem];
          setTransferInBtnDisabled(true);
          apis
            .downloadStudentData(
              transferInfoSHIS.pid,
              birth ?? DateTime.now(),
              {
                sid: sid ?? ' ',
                seat,
                year,
                sem,
                classGrade,
                classNo,
              }
            )
            .then(() => {
              setStudent(undefined);
              setTransfrInfo(undefined);
              setModalContent('轉入成功');
              setTransferRequestList([]);
              setTransferInBtnDisabled(false);
            })
            .catch((e) => {
              catchErrorForModal(e);
            });
        }}
        render={(props) => {
          const { handleSubmit } = props;
          return (
            <>
              <Row className="mb-2">
                <Col xs={1}>轉入學年：</Col>
                {Object.entries(semesters)
                  .filter(([k]) => k >= currentSemester)
                  .map(([n]) => {
                    return (
                      <Col key={`sem-${n}`} xs={1}>
                        <FormCheck
                          type="radio"
                          label={n}
                          name="setSemester"
                          checked={n === selectedSem}
                          onChange={(e) => {
                            setSelectedSem(n);
                          }}
                        />
                      </Col>
                    );
                  })}
              </Row>
              <Row className="mb-2">
                <Col xs={1}>轉入學號:</Col>
                <Col xs={5} className="mr-2">
                  <TextField property="sid" />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={1}>轉入年級:</Col>
                <Col xs={4} className="mr-2">
                  <InputTransformField
                    property="classGrade"
                    type="number"
                    min={min}
                    max={max}
                    transform={(v) =>
                      (+v >= min && +v <= max && I18N.Grades[+v]) || '未知年級'
                    }
                    validate={(v) => {
                      return +v >= min && +v <= max
                        ? undefined
                        : `範圍應在${min}~${max}之間`;
                    }}
                  />
                </Col>
                <Col xs={1}>轉入班級代號:</Col>
                <Col xs={2} className="mr-2">
                  <NumberField property="classNo" min={1} max={50} />
                </Col>
                <Col xs={1}>座號:</Col>
                <Col xs={2} className="mr-2">
                  <NumberField property="seat" min={1} max={99} />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <Button
                    onClick={handleSubmit}
                    disabled={transferInBtnDisabled}
                  >
                    轉入
                  </Button>
                </Col>
              </Row>
            </>
          );
        }}
      />
    </>
  ) : (
    <></>
  );

  useEffect(() => {
    setTransferDisabled(true);
    if (pidRef.current?.value) {
      if (birth) {
        setTransferDisabled(false);
      }
    }
  }, [pidRef.current?.value, birth]);

  return (
    <AuthedLayout>
      <Row className="mb-2">
        <div className="d-flex align-items-center">
          <Col xs={2}>學生身份證：</Col>
          <FormControl ref={pidRef} className="mr-2 wid-140" />
          <Col xs={2}>學生生日：</Col>
          <DateTimePicker
            containerClass="wid-140 mr-2"
            value={birth}
            onChange={(date) => {
              setBirth(date);
            }}
          />
          <Col xs={2}>
            <Button
              className="mr-2"
              disabled={transferDisabled}
              onClick={() => {
                if (pidRef.current && pidRef.current?.value.length > 5)
                  if (birth) {
                    apis
                      .getTransferInInfo(pidRef.current?.value, birth)
                      .then((t) => {
                        if (t.transferId == 1) {
                          // 舊轉新 下載舊系統學生資料
                          setTransferInfoSHIS(t);
                          setStudentSHIS(t.student || undefined);
                        } else {
                          setTransfrInfo(t);
                          setStudent(t.student || undefined);
                        }
                      })
                      .catch((e) => {
                        setTransfrInfo(undefined);
                        setStudent(undefined);
                        catchErrorForModal(e);
                        setTransferRequestList([]);
                      });
                  }
              }}
            >
              搜尋
            </Button>
          </Col>
          <Col xs={2}>
            <Button
              variant="secondary"
              disabled={!(pidRef.current?.value && birth)}
              onClick={() => {
                if (pidRef.current && pidRef.current?.value.length > 5)
                  if (birth) {
                    const localStorageKey = 'lastTransferRequest';
                    const lastClickDate = JSON.parse(
                      localStorage.getItem(localStorageKey) || '{}'
                    );
                    if (
                      lastClickDate[pidRef.current?.value ?? ''] !==
                      DateTime.now().toISODate()
                    )
                      toast
                        .promise(
                          apis.RequestTransferOut(pidRef.current?.value, birth),

                          {
                            success: '轉出申請通知函已寄出',
                            error: '轉出申請通知函失敗！請查看錯誤資訊。',
                            pending: '轉出申請通知函寄出中....',
                          }
                        )
                        .then(() => {
                          const storage = JSON.parse(
                            localStorage.getItem(localStorageKey) || '{}'
                          ) as Record<string, string>;
                          Object.entries(storage).forEach(([key, value]) => {
                            if (
                              DateTime.fromISO(value).diffNow('hours').hours <
                              -20
                            ) {
                              delete storage[key];
                            }
                          });
                          localStorage.setItem(
                            localStorageKey,
                            JSON.stringify({
                              ...storage,
                              [pidRef.current?.value ?? '']:
                                DateTime.now().toISODate(),
                            })
                          );
                          apis
                            .GetTransferRequestList(pidRef.current?.value ?? '')
                            .then((requests) => {
                              setTransferRequestList(
                                requests.filter((r): r is DateTime => r != null)
                              );
                            })
                            .catch(console.log);
                        })
                        .catch(catchErrorForModal);
                    else {
                      toast.error(`距離上次通知函寄出時間過短, 請於隔日再試。`);
                    }
                  }
              }}
            >
              寄送轉出申請通知函
            </Button>
          </Col>
        </div>
      </Row>
      {classGradeContent}
      {classGradeContentSHIS}
      <hr />
      <Row className="mb-2">
        <Col>{studentContent}</Col>
      </Row>
      <Row className="mb-2">
        <Col>{studentContentSHIS}</Col>
      </Row>
      <Modal
        show={!!modalContent}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        centered
      >
        <Modal.Header
          closeButton
          onHide={() => {
            setStudent(undefined);
            setTransfrInfo(undefined);
            setModalContent(undefined);
            if (pidRef.current) pidRef.current.value = '';
          }}
        >
          <Modal.Title id="contained-modal-title-vcenter">
            轉學生 - 轉入
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">{modalContent}</Modal.Body>

        <Modal.Footer>
          <Button
            onClick={() => {
              setStudent(undefined);
              setTransfrInfo(undefined);
              setModalContent(undefined);
              if (pidRef.current) pidRef.current.value = '';
            }}
          >
            關閉
          </Button>
        </Modal.Footer>
      </Modal>
    </AuthedLayout>
  );
};

export const TransferInPage = connector(transferInPage);
